import { useEffect, useMemo } from 'react';
import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { SignalKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  ClientHealthRange,
  GetClientHealthSignalsParams,
  GetClientHealthSignalsResponse
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { nextPageCursor } from '@src/Pages/ActivityFeed/useOracleEventsData';
import { OracleLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  CLIENT_HEALTH_SIGNALS_QUERY_KEY,
  ClientHealthSignalsFilter,
  RISK_FILTER_ALL
} from './client-health-constants';
import { signalsFilterFor } from './client-health-utils';

const fetchClientHealthSignals = async (
  params: GetClientHealthSignalsParams
): Promise<GetClientHealthSignalsResponse> => {
  const res = await OracleLambdaFetch('/recap/client-health/signals', { params });
  return awaitJSON<GetClientHealthSignalsResponse>(res);
};

const clientHealthSignalsQueryOptions = (
  cid: string,
  range: ClientHealthRange,
  signal: SignalKind,
  filter: ClientHealthSignalsFilter
) =>
  infiniteQueryOptions({
    queryKey: [CLIENT_HEALTH_SIGNALS_QUERY_KEY, cid, range, signal, filter],
    queryFn: ({ pageParam }) => {
      const { signalTag, ...rest } = filter;
      const base = { cid, range, ...rest, ...(pageParam && { cursor: pageParam }) };
      return fetchClientHealthSignals(
        signal === SignalKind.RISK
          ? { ...base, signal, ...(signalTag && { signalTag }) }
          : { ...base, signal }
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last, _allPages, _lastPageParam, allPageParams) =>
      nextPageCursor(last, allPageParams),
    enabled: Boolean(cid),
    meta: { suppressGlobalError: true }
  });

// Starts the unfiltered lists alongside the counts call instead of after it.
export const usePrefetchClientHealthSignals = (cid: string, range: ClientHealthRange) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!cid) return;
    [SignalKind.POSITIVE, SignalKind.RISK].forEach(signal => {
      const filter = signalsFilterFor(signal, null, RISK_FILTER_ALL);
      queryClient.prefetchInfiniteQuery(
        clientHealthSignalsQueryOptions(cid, range, signal, filter)
      );
    });
  }, [queryClient, cid, range]);
};

export const useClientHealthSignals = (
  cid: string,
  range: ClientHealthRange,
  signal: SignalKind,
  filter: ClientHealthSignalsFilter
) => {
  const query = useInfiniteQuery(clientHealthSignalsQueryOptions(cid, range, signal, filter));

  const signals = useMemo(
    () => query.data?.pages.flatMap(page => page.signals) ?? [],
    [query.data]
  );

  return {
    signals,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    error: query.error
  };
};

export type ClientHealthSignalsResult = ReturnType<typeof useClientHealthSignals>;
